// Angel (Stef mtg 8/20): Similar Articles editor — three boxes, the student pastes the article
// URL and gives the title that shows as the hyperlink. Serializes to markdown lines on the
// legacy "Nearest Neighbor Papers" pitch key (see lib/similarArticles.ts) so read views that
// render the field via RichText show hyperlinked titles unchanged.
import { useState } from "react";
import { Input, RequiredAsterisk } from "../../components/ui.js";
import {
  parseSimilarArticles,
  serializeSimilarArticles,
  similarArticleSlotMeta,
  type SimilarArticle,
} from "../../lib/similarArticles.js";

const SLOTS = [0, 1, 2];

export function SimilarArticlesBoxes({
  value,
  onChange,
  readOnly = false,
}: {
  value: string;
  onChange: (raw: string) => void;
  readOnly?: boolean;
}): JSX.Element {
  // Local rows so partial URLs don't get re-bucketed mid-keystroke; legacy lines beyond the
  // three slots are preserved verbatim on save.
  const [rows, setRows] = useState<SimilarArticle[]>(() => {
    const parsed = parseSimilarArticles(value);
    return SLOTS.map((i) => parsed[i] ?? { title: "", url: "" });
  });
  const [extra] = useState<SimilarArticle[]>(() => parseSimilarArticles(value).slice(SLOTS.length));

  const set = (i: number, key: keyof SimilarArticle, v: string): void => {
    const next = rows.map((r, j) => (j === i ? { ...r, [key]: v } : r));
    setRows(next);
    onChange(serializeSimilarArticles([...next, ...extra]));
  };

  return (
    <div className="space-y-1.5">
      {SLOTS.map((i) => {
        const r = rows[i]!;
        const href = r.url.trim()
          ? /^https?:\/\//i.test(r.url.trim())
            ? r.url.trim()
            : `https://${r.url.trim()}`
          : null;
        const slot = similarArticleSlotMeta(i);
        return (
          <div key={i} className="space-y-1 rounded-md border border-slate-200 p-2">
            <div className="text-xs font-medium text-slate-600">
              {slot.label}
              {slot.required ? <RequiredAsterisk /> : null}
            </div>
            <div className="grid gap-1.5 sm:grid-cols-2">
              <Input
                type="url"
                placeholder={`Article ${i + 1} — paste the URL`}
                aria-label={`${slot.label}${slot.required ? " (required)" : ""} URL`}
                value={r.url}
                onChange={(e) => set(i, "url", e.target.value)}
                readOnly={readOnly}
              />
              <Input
                placeholder="Title (shown as the link)"
                aria-label={`${slot.label}${slot.required ? " (required)" : ""} title`}
                value={r.title}
                onChange={(e) => set(i, "title", e.target.value)}
                readOnly={readOnly}
              />
            </div>
            {href && (
              <a
                className="block truncate text-xs text-brand hover:underline"
                href={href}
                target="_blank"
                rel="noreferrer"
              >
                {r.title.trim() || r.url.trim()} ↗
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
}
